import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Camera } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import api from '../../services/api'
import { petSitterService } from '../../services/petSitterService'
import { getAvatarUrl } from '../../utils/avatarHelpers'
import Avatar from '../../components/ui/Avatar'

export default function PerfilEditMobileExact() {
  const navigate = useNavigate()
  const { user, setUser, refreshUser } = useAuth()
  const { isPetSitter } = usePermissions()
  const fileInputRef = useRef(null)

  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    cedula: '',
    telefonoEmergencia: '',
    password: '',
    avatar: null,
    estilo_perfil: ''
  })
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user) return
    setForm((prev) => ({
      ...prev,
      nombre: user.nombre || '',
      apellido: user.apellido || '',
      email: user.email || '',
      telefono: user.telefono || '',
      cedula: user.cedula || '',
      telefonoEmergencia: user.telefonoEmergencia || user.telefono_emergencia || '',
      estilo_perfil: user.petSitters?.[0]?.estilo_perfil || user.pet_sitters?.[0]?.estilo_perfil || ''
    }))
    setAvatarPreview(getAvatarUrl(user))
  }, [user])

  const avatarPreviewUser = useMemo(() => {
    if (!user) return null
    if (!avatarPreview) return user
    // Avatar.jsx usa user.avatar | user.foto para la URL, por eso clonamos con el preview.
    return {
      ...user,
      avatar: avatarPreview,
      foto: avatarPreview,
      avatarUpdatedAt: avatarPreview
    }
  }, [user, avatarPreview])

  const onChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const onAvatarChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Selecciona una imagen válida.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar 5MB.')
      return
    }
    setError('')
    setForm((prev) => ({ ...prev, avatar: file }))
    const reader = new FileReader()
    reader.onload = () => setAvatarPreview(reader.result)
    reader.readAsDataURL(file)
  }

  const saveProfile = async () => {
    try {
      setSaving(true)
      setError('')

      const payload = new FormData()
      payload.append('nombre', form.nombre)
      payload.append('apellido', form.apellido)
      payload.append('telefono', form.telefono)
      if (form.cedula) payload.append('cedula', form.cedula)
      if (form.telefonoEmergencia) payload.append('telefonoEmergencia', form.telefonoEmergencia)
      if (form.password) payload.append('password', form.password)
      if (form.avatar) payload.append('avatar', form.avatar, form.avatar.name)

      await api.post('/auth/profile', payload)

      if (isPetSitter) {
        const estilo = form.estilo_perfil
        await petSitterService.updateMe({
          estilo_perfil: estilo === '' ? null : estilo
        })
      }

      const updated = await refreshUser()
      if (updated) setUser(updated)
      navigate('/perfil')
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'No se pudo guardar el perfil')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-full bg-white pb-20">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#555]"
          type="button"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-[16px] font-bold text-[#111]">Editar perfil</h1>
        <button
          onClick={saveProfile}
          disabled={saving}
          className="text-[#7a9a52] text-lg font-semibold disabled:opacity-50"
          type="button"
        >
          ✓
        </button>
      </div>

      <div className="px-4">
        <div className="flex justify-center py-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="relative h-[78px] w-[78px] rounded-full shadow-md overflow-hidden flex items-center justify-center text-white"
            type="button"
          >
            {avatarPreviewUser ? (
              <Avatar user={avatarPreviewUser} size="w-[78px] h-[78px] text-3xl" showStatus={false} />
            ) : null}
            <span className="absolute -bottom-0 -right-0 h-[24px] w-[24px] rounded-full bg-[#7a9a52] border-2 border-white flex items-center justify-center text-white">
              <Camera className="w-3 h-3" />
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onAvatarChange}
            className="hidden"
          />
        </div>

        <div className="pt-3 space-y-3">
          <Field label="Nombre">
            <input className="w-full rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white" name="nombre" value={form.nombre} onChange={onChange} />
          </Field>
          <Field label="Apellido">
            <input className="w-full rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white" name="apellido" value={form.apellido} onChange={onChange} />
          </Field>
          <Field label="Email">
            <input className="w-full rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none" name="email" value={form.email} readOnly />
          </Field>
          <Field label="Cédula">
            <input className="w-full rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white" name="cedula" value={form.cedula} onChange={onChange} />
          </Field>
          <Field label="Teléfono">
            <div className="flex items-center gap-2">
              <input className="w-[68px] rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3 py-2.5 text-[13px] text-[#666] outline-none" value="+598" readOnly />
              <input className="flex-1 rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white" name="telefono" value={form.telefono} onChange={onChange} />
            </div>
          </Field>
          <Field label="Teléfono de emergencia">
            <div className="flex items-center gap-2">
              <input className="w-[68px] rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3 py-2.5 text-[13px] text-[#666] outline-none" value="+598" readOnly />
              <input className="flex-1 rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white" name="telefonoEmergencia" value={form.telefonoEmergencia} onChange={onChange} />
            </div>
          </Field>
          {isPetSitter ? (
            <Field label="Estilo de cuidador">
              <select
                className="w-full rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white"
                name="estilo_perfil"
                value={form.estilo_perfil}
                onChange={onChange}
              >
                <option value="">No indicado</option>
                <option value="pro">Pro (certificaciones, formal)</option>
                <option value="onda">De onda (cercano, informal)</option>
              </select>
            </Field>
          ) : null}
          <Field label="Nueva contraseña">
            <div className="relative">
              <input
                className="w-full rounded-[12px] border border-[#eee] bg-[#f8f9fa] px-3.5 py-2.5 pr-10 text-[13px] text-[#333] outline-none focus:border-[#7a9a52] focus:bg-white"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={onChange}
                placeholder="Opcional"
              />
              <button
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#aaa]"
                type="button"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>

          {error ? <p className="text-[12px] text-[#e05555]">{error}</p> : null}

          <button
            onClick={saveProfile}
            disabled={saving}
            className="w-full h-12 rounded-[16px] bg-[#7a9a52] text-white font-bold disabled:opacity-50"
            type="button"
          >
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-bold text-[#111] mb-1.5">{label}</span>
      {children}
    </label>
  )
}
